import React, { useEffect, useState } from "react";
import { NameIdPair, UNITED_STATES } from "../../../models/misc";
import FeeSchedule from "../../../models/feeSchedule";
import QuickEntryTransactionCreatedCard from "./QuickEntryTransactionCreatedCard";
import { mergeLateArrivingBillingFields, QUICK_ENTRY_BILL_TO_DEFAULT_FIELDS } from "./quickEntryBillingDefaults";
import { CLASSIC_ZIP_REGEX, sanitizeFieldInput } from "./quickEntryFieldConstraints";
import { normalizePhone } from "../../../common/utils/appUtils";
import "./QuickEntryBillingPane.scss";

/**
 * Does the re-entered Digital Fax confirm the requestor contact's on-file fax?
 *
 * The gate is a CONFIRMATION check ("re-type the fax so we know it is right"), and the two values
 * it compares reach it in different shapes, which is what made an exact string compare wrong:
 *
 * - The ENTERED value is normalizePhone-formatted on every keystroke - in Classic because
 *   `BillingFormconstants.DIGITAL_FAX` is a member of `ReduxFormTextField`'s PHONE_FIELDS
 *   (ReduxFormTextField.tsx:8/40), and in Quick Entry because the input below delegates to
 *   `sanitizeFieldInput("phone", ...)`. Both panes also cap it at `maxLength={12}`, so the box can
 *   only hold `normalizePhone` of up to ten digits; the gate's own `length >= 12` condition then
 *   admits exactly one of those - the complete `###-###-####`. (A nine-digit entry is typeable,
 *   `703-555-131`, but rejected at eleven characters.) So the only entry that can ever clear the
 *   gate is a full ten-digit `###-###-####`.
 * - The ON-FILE value is the RAW stored string. Classic reads
 *   `values[RequestorFormConstants.FAX_NUMBER]`, seeded raw from
 *   `requestorDetails?.requestorInfo?.fax` (DataEntryDetailsRightPane.tsx:2795) or raw from the
 *   picked search row (:1683); Quick Entry reads the same field via
 *   `sendToAddress.fax` <- `DataEntryDetails.getQuickEntryBillingSendToAddress()`. `normalizePhone`
 *   is applied on NEITHER path.
 *
 * So for a contact whose stored fax is punctuated some other way - `703.555.1313`,
 * `202 555 0173`, or a bare `7035551313` - a formatted re-entry could never equal the stored
 * string, the gate could never clear, and Create Transaction stayed disabled with an error the
 * specialist had no keystroke to satisfy. Classic has the same defect; Quick Entry only
 * inherited it when the mask arrived, because before that the specialist could type the stored
 * punctuation verbatim.
 *
 * Measured against aspTest on 2026-08-17, over the 226,893 non-empty `tblRequestorContact.Fax`
 * values. Classified by running THIS function's `normalizePhone` and the `length >= 12` condition
 * over every distinct stored value, so the measurement uses the shipped mask rather than a SQL
 * re-implementation of it; an independent pure-SQL query reaches the same four numbers. The
 * queries, the classifier and their output are kept with the PR's evidence:
 *
 * - 225,825 are already canonical `###-###-####`, so they always confirmed.
 * - 497 carry exactly ten digits under other punctuation (`703.555.1313`, a bare `7035551313`,
 *   `(703)-555-1313`, `703-555-1313 x_____`). These are the rows this change unblocks -
 *   `normalizePhone` maps them onto the one value the masked box can produce.
 * - 118 hold no digit at all (`___-___-____` and similar), and still fail, as they SHOULD -
 *   there is no fax to confirm.
 * - 453 carry some other digit count and still cannot be confirmed: 191 carry one to nine digits
 *   (161 of them nine) and 262 carry more than ten (123 with fourteen, 66 with eleven, then a
 *   tail). Nine digits normalize to an eleven-character value the `length >= 12` condition
 *   refuses; more than ten normalize to the `###-###-####xEXT` extension form, longer than the
 *   `maxLength={12}` box. That is a real residual limitation, not something this change
 *   introduces or claims to fix; it needs a decision on the entry cap, so it is called out in
 *   the PR's Out of scope rather than papered over here.
 *
 * Comparing the two values AFTER the same formatter is what "the digits match" actually means, and
 * `normalizePhone` is idempotent, so an already-formatted entry is unchanged. This is strictly
 * unblocking: it never accepts a fax whose digits differ, and an on-file value with no digits at
 * all (`___-___-____`) still fails, as it must.
 */
export function digitalFaxMatchesOnFile(entered: string | undefined, onFile: string | undefined): boolean {
    const onFileFormatted = normalizePhone(onFile ?? "");
    return onFileFormatted.length > 0 && normalizePhone(entered ?? "") === onFileFormatted;
}

/**
 * AB#37973: Classic's own Bill To Zip validator, byte-for-byte - `zipRegex` in
 * localValidations.ts:336. Kept as one named constant so the parity is checkable at a glance.
 *
 * Now an alias of the shared CLASSIC_ZIP_REGEX (quickEntryFieldConstraints), because thread 53170
 * added the SAME validator to the requestor Zip field: one definition means the two zip fields
 * cannot drift apart from each other or from Classic. The name stays for the call site below.
 */
export const BILL_TO_ZIP_REGEX = CLASSIC_ZIP_REGEX;

/**
 * One additional-charge line item (Bug 35095 / AC2). Mirrors Classic's
 * AdditionalChargeRows row shape ({id, glCodeID, description, chargeAmount}) so
 * the QE pane can feed the SAME updateAdditionalChargesActionCreator save path
 * Classic uses - see DataEntryDetails.persistQuickEntryBillingFields.
 */
export interface QuickEntryAdditionalCharge {
    readonly id?: number;
    readonly glCodeID?: string;
    readonly description?: string;
    readonly chargeAmount?: string;
}

/**
 * Send-to address the "Same as send to" affordance (AC4) copies FROM. Classic
 * copies its SEND_TO_* redux-form fields into the BILL_TO_* fields; the QE pane
 * owns its own model, so the caller passes the current send-to values in.
 */
export interface QuickEntrySendToAddress {
    readonly requestorName?: string;
    readonly firstName?: string;
    readonly lastName?: string;
    readonly addressLine1?: string;
    readonly addressLine2?: string;
    readonly city?: string;
    readonly state?: string;
    readonly zip?: string;
    readonly phone?: string;
    readonly fax?: string;
    readonly email?: string;
}

export interface QuickEntryBillingFields {
    readonly feeScheduleId?: number;
    readonly releaseMethodId?: number;
    // --- Bug 35095 layout parity: Classic Fee Schedule / Method group controls ---
    /** Shipping Service - picker value is the service NAME (rides updateBillingPaneFields under Classic's `shippingService` key). */
    readonly shippingService?: string;
    /** "Charge for Postage?" radio value - "Default" | "Yes" | "No", mirroring Classic's RadioField (rides updateBillingPaneFields under Classic's `postageIsApplied` key). */
    readonly chargeForPostage?: string;
    /** Delivery Instructions - multiline (rides updateBillToRequestorData `data.deliveryInstructions`, encodeURI, like Classic). Replaces the F1d Delivery Address/Notes placeholders. */
    readonly deliveryInstructions?: string;
    /** Rule #7: "Re-enter Fax Number" - shown only when releaseMethodId === 3 (Digital Fax). Carried across Back-to-Quick-Entry. */
    readonly digitalFax?: string;
    // --- Bug 35095: Classic Mode billing feature-parity ---
    /** AC1: "Requires Prepayment?" - initialized from Classic's source (prePay). */
    readonly requiresPrepayment?: boolean;
    /** AC2: additional-charge line items (only meaningful when the selected fee schedule's billingTypeId === 1). */
    readonly additionalCharges?: readonly QuickEntryAdditionalCharge[];
    /** AC3 (AutoCertification ON): chosen certification template id - a GUID STRING (matches Classic's certificationTypeId?: string, RightPane 214), NOT a number; undefined once "Remove Certification" is used. */
    readonly certificationTemplateId?: string;
    /** AC3 (AutoCertification OFF): "Certification Requested?" flag. */
    readonly certificationRequested?: boolean;
    // AC4: Bill To address set (PII/PHI - never logged; see hipaa-compliance.md).
    readonly billToRequestorName?: string;
    readonly billToFirstName?: string;
    readonly billToLastName?: string;
    readonly billToAddressLine1?: string;
    readonly billToAddressLine2?: string;
    readonly billToCity?: string;
    readonly billToState?: string;
    readonly billToZip?: string;
    readonly billToPhone?: string;
    readonly billToFax?: string;
    readonly billToEmail?: string;
    /** AC4: "Same as send to" - copies the caller-supplied send-to address into the bill-to fields. */
    readonly sameAsSendTo?: boolean;
}

export interface QuickEntryBillingTransactionCreated {
    readonly requestId: string;
}

export interface QuickEntryFeatureFlags {
    readonly AutoCertification?: boolean;
}

export interface QuickEntryBillingPaneProps {
    readonly feeSchedules: readonly FeeSchedule[];
    readonly releaseMethods: readonly NameIdPair<number>[];
    /** " Back to Quick Entry" (AC8, Business Rule 13) - no re-submission of the bulk commit; entered values are preserved by the caller (DataEntryDetails.tsx keeps this pane mounted-but-hidden, not unmounted). */
    readonly onBack: () => void;
    readonly onCreateTransaction: (fields: QuickEntryBillingFields) => void;
    readonly onCreateAndContinue: (fields: QuickEntryBillingFields) => void;
    /**
     * Preserves entered values across a Back-to-Quick-Entry round trip (Business Rule 13 / Test 4.2) - the caller owns this state, not this component's own local state, precisely so it survives an unmount/remount of this pane.
     *
     * AB#37853: these MAY ARRIVE LATE - after this pane has already mounted. Do not assume the
     * value present at mount is final; see the syncing effect in the component body.
     */
    readonly initialFields?: QuickEntryBillingFields;
    readonly onFieldsChange?: (fields: QuickEntryBillingFields) => void;
    /**
     * AB#35088 (QA retest finding 2): incremented by the parent each time it adopts a release
     * method the SERVER recomputed from the requestor contact. A bump - and only a bump - makes
     * this pane take `initialFields.releaseMethodId` even though it already holds a value; without
     * one the fill-only-what-is-unset precedence governs, so an in-progress pick is never clobbered
     * by a default that merely arrived late. See the effect in the component body.
     */
    readonly releaseMethodResyncSignal?: number;
    /** AB#37981 round 2: bumped when the parent adopts a change the SERVER made to this
     *  transaction's bill-to record. Same contract as releaseMethodResyncSignal above. */
    readonly billToRecordResyncSignal?: number;
    /**
     * AC9: once set (only after "Create Transaction", not "Create & Continue"
     * - see DataEntryDetails.tsx's onQuickEntryCreateTransaction doc comment),
     * replaces the form/footer with master-demo-v6.html's `.txn-created`
     * success view - Request ID + auto-advance message + manual "Next Queue
     * Item" button.
     */
    readonly transactionCreated?: QuickEntryBillingTransactionCreated;
    readonly onNextQueueItem?: () => void;
    // --- Bug 35095: Classic Mode billing feature-parity inputs ---
    /** AC3: gates certification UI (dropdown when AutoCertification ON, checkbox when OFF) - same flag Classic reads. */
    readonly featureFlags?: QuickEntryFeatureFlags;
    /** AC3: certification template options (state.dataEntryState.certificationTypes) - same source as Classic; ids are GUID strings. */
    readonly certificationTypes?: readonly NameIdPair<string>[];
    /** AC2: GL-code options for additional-charge rows (state.dataEntryState.additionalChargeType) - same source Classic feeds AdditionalChargeRows. */
    readonly additionalChargeTypeOptions?: readonly NameIdPair<number>[];
    /** Layout parity: Shipping Service options (state.dataEntryState.shippingService picker list, value=name) - same source Classic feeds the Shipping dropdown. */
    readonly shippingServices?: readonly { readonly label: string; readonly value: string }[];
    /** Rule #9: internal-request transactions lock Fee Schedule / Release Method / Shipping / Postage (transactionDetails.transactionRequest.isInternalRequest). */
    readonly isInternalRequest?: boolean;
    /** AC4: send-to values the "Same as send to" checkbox copies into the bill-to fields. */
    readonly sendToAddress?: QuickEntrySendToAddress;
    /**
     * AB#37853 (Phase 13): true while the post-Confirm-All billing defaults are in flight. Shows a
     * "Loading..." placeholder on Fee Schedule / Release Method and disables them, so the window
     * before the values arrive is not mistaken for "nothing is coming - pick one yourself".
     *
     * ADDITIVE to the Internal lock, never a replacement (see disableFeeSchedule below). The caller
     * guarantees it cannot stay true indefinitely via a bounded timeout - a stuck value here would
     * disable exactly the two controls this Bug exists to make usable.
     */
    readonly defaultsPending?: boolean;
    /**
     * AB#38349: true while this pane's `releaseMethodId` is KNOWN to be stale with respect to the
     * requestor contact currently shown on the Quick Entry row - i.e. the row's contact is not the
     * contact the transaction is linked to, which is what the server derived the release method
     * from. Only the parent can know that (it holds both ids); see
     * DataEntryDetails.isQuickEntryReleaseMethodStaleForContact.
     *
     * Narrowly scoped on purpose. It suppresses ONE assertion - the Download-needs-an-email
     * warning, whose two operands have different vintages while this is true - and shows a
     * re-check note in its place. It does NOT relax `creationDisabled`, and it does not reuse
     * `defaultsPending`: that flag additionally DISABLES Fee Schedule and Release Method, which
     * would take the Release Method dropdown away from the operator during a window that can last
     * until Confirm All, and Create Transaction is gated on that very field.
     */
    readonly releaseMethodStale?: boolean;
}

/** AC2: two-decimal normalization for a charge amount, mirroring Classic's numericAmount formatting. Blank/non-numeric -> "". */
function formatChargeAmount(raw: string): string {
    if (raw === undefined || raw === null || `${raw}`.trim() === "") {
        return "";
    }
    const parsed = Number(`${raw}`.replace(/[^0-9.\-]/g, ""));
    return Number.isNaN(parsed) ? "" : parsed.toFixed(2);
}

/**
 * Quick Entry's Billing pane (Spec #34102 / F1d, AC7/AC8/AC9, Business Rules
 * 11-12; extended for Bug 35095 to reach Classic Mode feature-parity).
 * Manual entry only - no AI pre-fill (AC7); reuses the same
 * FEE_SCHEDULES/RELEASE_METHODS option sources the classic Billing tab
 * already fetches via getFacilityFeeSchedules/getFacilityReleaseMethods, but
 * persists via the existing UpdateTransaction ("updateBillingPaneFields")
 * and BillingComplete finalization actions rather than a new endpoint
 * (Technical Design "reuse, not a new endpoint").
 *
 * Bug 35095: this pane now also owns prepayment, additional charges,
 * certification, and the full bill-to address set (previously Classic-only).
 * These live in the caller-owned QuickEntryBillingFields model (NOT the Classic
 * redux-form data path) so they survive a Back-to-Quick-Entry round trip.
 */
/**
 * AB#35088 (QA retest, second finding): what the two defaulted controls say while the post-Confirm-All
 * values are in flight, and the note rendered beside them.
 *
 * Exported so the tests, and anything that quotes the wording, cannot drift from what renders. The
 * placeholder names the SOURCE of the pending value because that is what the specialist is actually
 * waiting on - the purpose has to persist before the server can reassign the fee schedule - and
 * because "Loading..." alone was what QA read as empty.
 */
export const BILLING_DEFAULTS_PENDING_PLACEHOLDER = "Applying billing defaults\u2026";
/**
 * AB#38349: what the pane says in place of the Download-needs-an-email warning while its release
 * method is stale for the contact just selected. Exported so the tests cannot drift from it.
 *
 * REVIEW ROUND 4 (Major, both reviewers): the first wording - "Release Method is being re-checked
 * ... until it settles", rendered with the AB#35088 spinner - asserted a round trip that is NOT in
 * flight. The 2026-08-24 measurement is that the contact PATCH does not re-point the transaction's
 * link, so the reconcile it triggers brings back the SAME release method; nothing clears this state
 * except Confirm All re-pointing the link (persistQuickEntryRequestorResolution's
 * transactions/sendToContact), and Confirm All is a button on the previous step that the note never
 * mentioned. A specialist was told to wait for something that would not happen, having lost the one
 * message that named a remedy.
 *
 * So the note now states (a) why the value on screen cannot be trusted for this contact, (b) the
 * event that re-checks it, and (c) what to do to create WITHOUT waiting for that event - the two
 * remedies Classic's red message implies. Every clause is true whatever the operator does next,
 * which is what makes it safe even if the state persists.
 */
const RELEASE_METHOD_RECHECK_LEAD =
    "Release Method still reflects the previously linked requestor contact, and is re-checked when "
    + "you Confirm All. To create this transaction now, ";
export const RELEASE_METHOD_RECHECK_NOTE = RELEASE_METHOD_RECHECK_LEAD
    + "add an email address to the selected contact or choose a different Release Method.";
/**
 * AB#38349 (review round 6): the same note for the Digital Fax gate, which pairs the same two
 * vintages one field over. Only the remedy differs, so only the remedy is written twice - the lead
 * is shared so the two cannot drift into describing the staleness differently.
 */
export const RELEASE_METHOD_RECHECK_NOTE_DIGITAL_FAX = RELEASE_METHOD_RECHECK_LEAD
    + "re-enter the selected contact\u2019s fax number or choose a different Release Method.";
export const BILLING_DEFAULTS_PENDING_NOTE =
    "Fee Schedule and Release Method are being applied from the request purpose. "
    + "They will fill in automatically \u2014 no need to pick one.";

const QuickEntryBillingPane: React.FC<QuickEntryBillingPaneProps> = (props) => {
    const [fields, setFields] = useState<QuickEntryBillingFields>(props.initialFields ?? {});
    // Bug fix (AB#37853, D1): initialFields MAY ARRIVE AFTER MOUNT. The useState above runs only
    // on first mount, and Confirm All flips to this tab synchronously while the billing re-fetch
    // that supplies the defaults resolves asynchronously (DataEntryDetails: the tab flip at
    // :5504-5508 vs. the chained re-fetch at :5411-5413), so the pane routinely mounts on `{}`
    // and used to keep it forever. For an Internal request that is a hard deadlock: Fee Schedule
    // and Release Method are locked below (Rule #9) and `creationDisabled` needs exactly those
    // two values, so nothing the operator can do unblocks Create Transaction.
    //
    // Classic's counterpart is DataEntryDetailsRightPaneBillingSection 40-49 - the same
    // effect-keyed-on-the-incoming-prop shape - but Classic overwrites unconditionally, which is
    // safe only because Classic autosaves every change. QE defers all persistence to Confirm All,
    // so the merge fills ONLY keys that are still unset and leaves in-progress edits alone.
    //
    // Deps are props.initialFields ALONE, deliberately. Adding `fields` would re-run the merge
    // after every local edit, and a field the operator had just cleared would look "unset" again
    // and be re-filled from the prop - reintroducing the clobber this fix exists to prevent.
    // mergeLateArrivingBillingFields returns null when nothing changes, which is what stops the
    // parent/child echo (the parent hands back down what this child pushed up) from looping.
    useEffect(() => {
        const merged = mergeLateArrivingBillingFields(fields, props.initialFields);
        if (merged !== null) {
            setFields(merged);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.initialFields]);
    // AB#38349 (review round 4): has the OPERATOR chosen Release Method in this pane's own dropdown
    // since the last server-derived value landed? The staleness suppression below is only defensible
    // while the displayed release method came from the server (derived from the transaction's linked
    // contact, which is what makes it a different vintage from `sendToAddress`). A value the operator
    // picked describes the contact on the row, so a Download-without-an-email warning against it is
    // simply correct and must not be hidden behind a note about a re-check.
    //
    // Round 5: this covers the CURRENT mount only, and the reported flow spans two. The pane is
    // conditionally mounted, and the controls that change the requestor contact are on the Request
    // Info pane, so the operator has to leave Billing - unmounting this - before `releaseMethodStale`
    // can even become true. The durable copy therefore lives on the parent
    // (DataEntryDetails' quickEntryReleaseMethodPickedByOperator), which ANDs it into the
    // `releaseMethodStale` prop; the two are cleared by the same event, a server re-derive.
    const [releaseMethodPickedByOperator, setReleaseMethodPickedByOperator] = useState(false);
    // Bug fix (AB#35088, QA retest finding 2): the merge above fills only keys that are still
    // UNSET, so a release method the server RECOMPUTED after a requestor-contact change could not
    // reach this pane once a value had already landed here - Billing kept Digital Fax while the
    // Summary pane (which reads transactionDetails directly) correctly moved to Download, and
    // Create Transaction would then persist the fax method for a contact with no fax on file.
    //
    // Keyed on the PARENT'S SIGNAL, not on the incoming value changing. That distinction is the
    // whole design: from inside this pane, "the parent handed me a release method that differs
    // from mine" is ambiguous - it is either a recomputation to adopt or a first-arriving default
    // racing an in-progress pick, and Tests 3.1 and 13.5 pin that the second must NOT clobber.
    // Only the parent can tell them apart (computeQuickEntryReleaseMethodResync acts solely on a
    // change in the transaction's own value), so it bumps this counter in the same setState that
    // adopts, and only that bump overrides local state. A counter rather than the value itself so
    // a recomputation BACK to an earlier value is still a signal.
    useEffect(() => {
        const incoming = props.initialFields?.releaseMethodId;
        if (incoming == null) {
            return;   // nullish, not falsy - and never clear what the operator has
        }
        // Identity-preserving when it already agrees, so React bails out rather than re-rendering.
        setFields(prev => prev.releaseMethodId === incoming ? prev : { ...prev, releaseMethodId: incoming });
        // AB#38349: the value on screen is server-derived again, so the suppression below is back in
        // scope. Reset here rather than on the value changing - a resync that re-adopts the SAME id
        // still means the server, not the operator, now owns what is displayed.
        setReleaseMethodPickedByOperator(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.releaseMethodResyncSignal]);
    // Bug fix (AB#37981 round 2): the same problem one block down. The merge above fills only keys
    // that are still UNSET, so once a Bill To value has landed here a record the SERVER rewrote
    // could not reach the screen. Why that matters is in computeQuickEntryBillToRecordResync.
    //
    // Keyed on the PARENT'S SIGNAL for the same reason as the release-method effect above: from in
    // here, "the parent handed me a value that differs from mine" cannot be told apart from a
    // first-arriving default racing an in-progress edit. The parent decides; this displays.
    useEffect(() => {
        if (!props.billToRecordResyncSignal) {
            return;   // 0 is the initial value, not an adoption - never act on the mount pass
        }
        const incoming = props.initialFields;
        if (!incoming) {
            return;
        }
        setFields(prev => {
            const adopted: Partial<QuickEntryBillingFields> = {};
            QUICK_ENTRY_BILL_TO_DEFAULT_FIELDS.forEach(field => {
                const value = incoming[field];
                // Blank never clears what is on screen - the same rule the seed and the parent's
                // re-sync both apply. The parent has already decided ownership; this is display.
                if (typeof value !== "string" || value.trim() === "" || prev[field] === value) {
                    return;
                }
                (adopted as Record<string, unknown>)[field] = value;
            });
            // Identity-preserving when nothing differs, so React bails out rather than re-rendering.
            return Object.keys(adopted).length ? { ...prev, ...adopted } : prev;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.billToRecordResyncSignal]);
    // MEDIUM-2 (#7): digital-fax is a validation GATE, not a display field. On blur we
    // compare the re-entered fax against the requestor's on-file contact fax and surface
    // a mismatch error inline (mirrors Classic onBlurDigitalFaxField, RightPane 1659-1673).
    const [digitalFaxError, setDigitalFaxError] = useState<string>("");
    // Layout parity: Classic renders Additional Charges as a collapsible accordion
    // (Accordion.Toggle / Accordion.Collapse, Accordion.tsx 681-722), collapsed by
    // default. Mirror that presentation here with a local open/closed toggle.
    const [additionalChargesOpen, setAdditionalChargesOpen] = useState<boolean>(false);

    const update = (partial: Partial<QuickEntryBillingFields>) => {
        const next = { ...fields, ...partial };
        setFields(next);
        props.onFieldsChange?.(next);
    };

    // #7: validate the re-entered digital fax against the requestor's on-file fax on blur
    // (Classic's trigger). Match/empty clears the error; mismatch shows Classic's message.
    const onDigitalFaxBlur = () => {
        const onFileFax = props.sendToAddress?.fax;
        if (fields.digitalFax && onFileFax && !digitalFaxMatchesOnFile(fields.digitalFax, onFileFax)) {
            setDigitalFaxError("Fax number entered does not match the assigned requestor contact fax number");
        } else {
            setDigitalFaxError("");
        }
    };

    // AC2: additional charges only apply when the selected fee schedule's
    // billingTypeId === 1 (identical gate to Classic - DataEntryDetailsRightPane
    // getBillingTypeId() == 1).
    const selectedFeeSchedule = props.feeSchedules.find(fs => fs.id === fields.feeScheduleId);
    // Rule #10: the Bill To section AND additional charges only apply when the selected
    // fee schedule's billingTypeId === 1 - Classic gates the whole accordion (bill-to +
    // charges) on getBillingTypeId() == 1 (DataEntryDetailsRightPane 2821).
    const billingTypeOne = selectedFeeSchedule?.billingTypeId === 1;
    const additionalChargesEnabled = billingTypeOne;
    const additionalCharges = fields.additionalCharges ?? [];

    const autoCertification = Boolean(props.featureFlags?.AutoCertification);
    // Classic disables "Remove Certification" until a template is actually chosen.
    const removeCertificationDisabled = fields.certificationTemplateId === undefined || fields.certificationTemplateId === null;

    // Rule #9 (internal request): fee schedule, release method, shipping + postage are
    // locked. Rule #6/#8 (release method 2 = Fax): shipping + postage disabled. Mirrors
    // DataEntryDetailsRightPaneBillingSection's disableShippingDropdown / internal-request
    // effect (42, 57-66) - shipping and postage share the same disabled flag in Classic.
    const isInternalRequest = Boolean(props.isInternalRequest);
    // AB#37853 (Phase 13): the post-Confirm-All defaults are in flight. ORed with the Internal lock,
    // never substituted for it - Internal must stay locked after loading finishes, which is what
    // keeps Rule #9 (and the existing Test 9.2) intact. Only these two controls are affected: they
    // are the ones the re-fetch supplies, and the ones the creation gate needs.
    const defaultsPending = Boolean(props.defaultsPending);
    const disableFeeSchedule = isInternalRequest || defaultsPending;
    const disableReleaseMethod = isInternalRequest || defaultsPending;
    // Distinguishes "a value is on its way" from "- Select -", which reads as "nothing is coming,
    // pick one yourself" and invites an interaction that is about to be overwritten on screen.
    //
    // AB#35088 (QA retest, second finding): QA measured this state lasting 8-18 SECONDS and reported
    // that it "reads as empty". A bare "Loading..." on a disabled control is what makes it read that
    // way - it names a state, not a reason, so a specialist watching it for fifteen seconds cannot
    // tell whether the app is working or stuck. The wait itself is the Confirm All persist gate and
    // is NOT shortened here (see the note at the arming site in DataEntryDetails); what changes is
    // that the pane now says what it is waiting for.
    // AB#39265 (David Heifetz, PR !10691): what the Fee Schedule control DISPLAYS, which is not
    // always what `fields` holds. A controlled <select> whose value matches no <option> does not
    // render blank - the browser falls back to the FIRST option - so after a purpose change
    // withdrew the drafted schedule the pane showed one the operator never picked, and Create
    // Transaction would have persisted the hidden original. Measured, not assumed: the DOM value
    // moved from 5000212 to 30.
    //
    // DERIVED rather than cleared, which is the important part. An earlier version of this fix
    // wrote `undefined` back into `fields` and needed two guards to survive it: a lock exemption
    // (clearing a disabled control strands an Internal re-save with a required field empty and
    // both Create buttons dead) and a dependency on that lock (or a list arriving mid-lock was
    // dropped rather than deferred). Both are gone. Deriving also covers the one path the clear
    // could never reach - an Internal request, where the lock NEVER lifts - and it cannot disturb
    // the seeder: nothing is written, so the parent's mirror is not replaced and
    // computeQuickEntryBillingDefaults' `alreadySeeded` ledger is untouched, which is what kept a
    // clear from re-opening the field and letting the server's schedule overwrite the operator's
    // pick. `mergeLateArrivingBillingFields`' "a deliberate CLEAR stays cleared" contract is
    // likewise no longer in play.
    //
    // A 0 id means "none" (Business Rule 2, Test 4.3) and has no option either, so it displays the
    // placeholder exactly as it did before - no special case needed.
    //
    // `creationDisabled` deliberately keeps consuming the RAW `fields.feeScheduleId`, the same
    // display/gate split the Download-email and Digital-Fax rules above make and for the same
    // reason. It does mean an unmatched id can still be persisted; that write is pre-existing, is
    // not made more likely by this change, and blocking on it needs a product call about the
    // Internal case, where the control is locked and the operator has nothing to pick. Recorded as
    // a follow-up on AB#39265 rather than decided here.
    const feeScheduleIdOnOffer = props.feeSchedules.some(fs => fs.id === fields.feeScheduleId)
        ? fields.feeScheduleId
        : "";
    const defaultedControlPlaceholder = defaultsPending ? BILLING_DEFAULTS_PENDING_PLACEHOLDER : "- Select -";
    const disableShippingAndPostage = isInternalRequest || fields.releaseMethodId === 2;
    // Rule #7 (release method 3 = Digital Fax): reveal the Re-enter Fax Number field.
    const showDigitalFax = fields.releaseMethodId === 3;

    // Rule #11: a charge row is incomplete until type + description + amount are all set.
    const chargeRowIncomplete = (c: QuickEntryAdditionalCharge) =>
        !c.glCodeID || !c.description || !`${c.description}`.trim() || !c.chargeAmount;
    // Rule #11: the Add Charge button is disabled while any existing row is incomplete
    // (mirrors AdditionalChargeRows setEnable(hasIncomplete)).
    const addChargeDisabled = additionalCharges.some(chargeRowIncomplete);

    // AC5: bill-to required fields gate creation - but ONLY when the bill-to section is
    // shown (rule #10). Required set matches Classic's required BILL_TO_* fields
    // (requestor name, address line 1, city, state, zip).
    const billToRequiredValues = [
        fields.billToRequestorName,
        fields.billToAddressLine1,
        fields.billToCity,
        fields.billToState,
        fields.billToZip,
    ];
    const billToInvalid = billingTypeOne && billToRequiredValues.some(v => !v || `${v}`.trim() === "");
    // AB#37973: Bill To Zip needs a validation ERROR, which is what the bug asked for and what
    // Classic already does - localValidations.ts:336/353, on the Billing tab and under the same
    // billingTypeId==1 gate as the required-field checks above:
    //   const zipRegex = /^\d{5}(-\d{4})?$/;
    //   else if (billZip && !zipRegex.test(billZip)) errors[BILL_TO_ZIP] = "Enter valid zip code."
    // Quick Entry only ever checked non-emptiness, so "123" passed the gate and was saved with no
    // message - the bug's Actual Result, still reproducing.
    //
    // Post-hoc validation, NOT a keystroke mask. An earlier revision of this branch masked the
    // field instead and had to be reverted: stripping to digits-and-hyphen turned a Canadian
    // "K1A 0B1" into "101" and saved it silently, with no keystroke that could produce the right
    // value. This tells the specialist their input is wrong and lets them fix it, which is both
    // what the bug asked for and the only escapable design.
    //
    // Consequence worth stating plainly: a non-US billing zip is now rejected here, as it already
    // is in Classic. Measured against aspTest on 2026-08-17 by running the regex above over every
    // distinct stored value, of 510,510 non-empty `tblRequestorBilling.ZipCode` values 15,212 fail
    // it today - 14,527 contain a letter (`M5V 2A5` and the like) and 685 are digits in some other
    // shape. The query and classifier are kept with the PR's evidence.
    // Widening the accepted alphabet is a product decision about Classic's regex, not something
    // to diverge on in one pane.
    const billToZipEntered = `${fields.billToZip ?? ""}`.trim();
    const billToZipFormatInvalid = billingTypeOne
        && billToZipEntered.length > 0
        && !BILL_TO_ZIP_REGEX.test(billToZipEntered);
    // AC5: each SHOWN additional-charge row must have type + description + amount.
    const chargeRowsInvalid = additionalChargesEnabled && additionalCharges.some(chargeRowIncomplete);

    // AB#35095 (validation-parity gap): Classic blocks Billing -> Records when Release
    // Method is Download (id 2) and the requestor contact has no email
    // (localValidations.ts:368-372). "Create Transaction" and "Create & Continue" both
    // move the transaction into the SAME records-ready state server-side (only the UI
    // hand-off differs), so both must be gated the same way Classic gates its one
    // Next button.
    const missingContactEmailForDownload = fields.releaseMethodId === 2
        && !(props.sendToAddress?.email && props.sendToAddress.email.trim());

    // AB#38349: the two operands above have DIFFERENT VINTAGES after a contact change.
    // `props.sendToAddress` is the live redux-form value (writeContactToForm runs synchronously on
    // the pick); `fields.releaseMethodId` is server-derived from the contact the TRANSACTION is
    // linked to, and Quick Entry re-points that link only at Confirm All. So while the parent
    // reports the release method stale for the row's contact, this expression is about a contact
    // that is no longer selected and must not be rendered as a validation failure - QA's Expected:
    // "...show a loading state on the affected fields instead of a red error on stale data." The
    // second half is what is honoured here; the first is not, because there is nothing to load -
    // see RELEASE_METHOD_RECHECK_NOTE for what stands in its place and why it carries no spinner.
    //
    // Display only. `creationDisabled` below still consumes the RAW value, deliberately: the rule
    // may be unprovable right now, but it is not satisfied either, and DataEntryDetails'
    // persistence-time backstop refuses the create with this same message regardless. Withholding
    // the message while enabling the button would invite a click that is guaranteed to be refused.
    // ...and only while the value on screen is still the SERVER's (see releaseMethodPickedByOperator).
    const releaseMethodStaleForContact = Boolean(props.releaseMethodStale) && !releaseMethodPickedByOperator;
    const showMissingContactEmailWarning = missingContactEmailForDownload && !releaseMethodStaleForContact;

    // AB#35095 (validation-parity gap): Classic also blocks Billing -> Records for
    // Digital Fax (id 3) unless the re-entered fax is present (>=12 chars) AND matches
    // the requestor's on-file fax (localValidations.ts:374-387) - required even if
    // nothing was ever typed, and invalid even when there's no on-file fax to match
    // against. `onDigitalFaxBlur`/`digitalFaxError` above stay as the blur-triggered
    // inline hint; this is the declarative, always-current source of truth for the
    // actual gate, matching Classic's rule regardless of blur timing.
    const digitalFaxInvalid = showDigitalFax && (() => {
        const entered = fields.digitalFax ?? "";
        if (entered.length < 12) {
            return true;
        }
        // Compared after the same formatter both values would get on entry - see
        // digitalFaxMatchesOnFile above for why an exact string compare made this gate
        // unsatisfiable for a contact whose stored fax is punctuated any other way.
        return !digitalFaxMatchesOnFile(entered, props.sendToAddress?.fax);
    })();

    // AB#38349 (review round 6, consistency): the Digital Fax gate is the SAME mixed-vintage
    // pairing, one field over. `showDigitalFax` comes from the server-derived
    // `fields.releaseMethodId` - the transaction's LINKED contact - while the fax it demands is
    // `props.sendToAddress.fax`, the live value for the contact the row now shows. After a contact
    // change the pane can therefore reveal "Re-enter Fax Number" for a delivery method the server
    // would not have chosen for the selected contact, and demand a confirmation nothing can supply:
    // digitalFaxMatchesOnFile refuses an on-file value that carries no ten-digit number (the 118
    // `___-___-____` rows and the 453 other-digit-count rows measured above), so a mistyped-looking
    // red error appears that no keystroke clears - and where the new contact has no fax string at
    // all, `onDigitalFaxBlur`'s `onFileFax &&` guard leaves the pane silent with both Create buttons
    // disabled and nothing on screen saying why. Both are the defect this branch exists to fix.
    //
    // Same treatment as the Download rule above, deliberately: withhold the assertion, say what is
    // actually going on, and leave `creationDisabled` on the RAW value. What that gives up is the
    // blur message's specificity in the one sub-case where the new contact DOES have a confirmable
    // fax and the operator simply mistypes it; the note names re-entering the fax as a remedy and
    // clears the moment the entry confirms, so the feedback loop survives. A second, narrower rule
    // to keep that message is not worth the divergence - and because this reuses
    // `releaseMethodStaleForContact`, a Digital Fax the OPERATOR picked in this pane's own dropdown
    // is not suppressed at all and still gets the precise message.
    const showDigitalFaxMismatchError = Boolean(digitalFaxError) && !releaseMethodStaleForContact;
    // Which remedy the note names, and - PR !10141 review finding 2 - WHERE it is rendered. The two
    // blockers are mutually exclusive (one needs release method 2, the other release method 3), so
    // there is never a choice to make between them; but they are blockers on two DIFFERENT controls,
    // and a single note in the Release Method block put the fax remedy a field away from the fax
    // input whose red message it had just removed. Each variant is therefore rendered in the block
    // that owns the control it is about. The shared lead sentence is still written once
    // (RELEASE_METHOD_RECHECK_LEAD), so the two cannot drift.
    const releaseMethodRecheckNote = releaseMethodStaleForContact && missingContactEmailForDownload
        ? RELEASE_METHOD_RECHECK_NOTE
        : undefined;
    const digitalFaxRecheckNote = releaseMethodStaleForContact && digitalFaxInvalid
        ? RELEASE_METHOD_RECHECK_NOTE_DIGITAL_FAX
        : undefined;

    // Business Rule 11 / AC5/AC8 (+ AB#35095 validation parity): creation stays disabled
    // until Fee Schedule + Release Method are populated AND every shown required field
    // is valid, AND the release-method-specific delivery requirement is satisfied.
    const creationDisabled = !fields.feeScheduleId || !fields.releaseMethodId || billToInvalid || chargeRowsInvalid
        || missingContactEmailForDownload || digitalFaxInvalid || billToZipFormatInvalid;

    const updateCharge = (index: number, partial: Partial<QuickEntryAdditionalCharge>) => {
        const nextCharges = additionalCharges.map((c, i) => (i === index ? { ...c, ...partial } : c));
        update({ additionalCharges: nextCharges });
    };

    const addCharge = () => {
        update({ additionalCharges: [...additionalCharges, {}] });
    };

    const removeCharge = (index: number) => {
        update({ additionalCharges: additionalCharges.filter((_c, i) => i !== index) });
    };

    // Rule #3: confirm before clearing the certification template (mirrors Classic's
    // window.confirm("Remove certification?"), DataEntryDetailsRightPane 1364-1368).
    const onRemoveCertification = () => {
        if (typeof window !== "undefined" && !window.confirm("Remove certification?")) {
            return;
        }
        update({ certificationTemplateId: undefined });
    };

    // AC4: "Same as send to" copies the caller-supplied send-to address into the bill-to
    // fields (mirrors Classic's onCheck copy).
    //
    // Rule #4 ("UNCHECKING clears the bill-to fields") is DELIBERATELY no longer implemented, and
    // the citation it rested on was wrong. Classic's clearing branch
    // (DataEntryDetailsRightPane onCheck, the `else` at 812-825) is dead code: the checkbox is a
    // redux-form Field rendering ReduxFormcheckboxField, which calls
    // `onCheck && onCheck(value)` with the React ChangeEvent, not a boolean
    // (ReduxFormCheckboxField.tsx:11-21, and CheckboxField.tsx passes MUI's event straight
    // through). A SyntheticEvent is always truthy, so Classic's `if (value)` takes the COPY branch
    // on an untick as well - which is why unticking in Classic leaves the Bill To data on screen
    // (reported from hand testing, review round 6 thread 53046).
    //
    // We do not copy that accident: re-copying on untick would let an untick silently overwrite
    // Bill To with whatever Requestor Info now holds. We match the observable outcome instead -
    // untick only unticks. Clearing was also actively harmful now that AB#37981 auto-populates
    // Bill To from the transaction's own bill-to record: the cleared values are that record, and
    // the per-field `alreadySeeded` bookkeeping means they never come back for the session.
    const onToggleSameAsSendTo = (checked: boolean) => {
        if (checked && props.sendToAddress) {
            const s = props.sendToAddress;
            update({
                sameAsSendTo: true,
                billToRequestorName: s.requestorName ?? "",
                billToFirstName: s.firstName ?? "",
                billToLastName: s.lastName ?? "",
                billToAddressLine1: s.addressLine1 ?? "",
                billToAddressLine2: s.addressLine2 ?? "",
                billToCity: s.city ?? "",
                billToState: s.state ?? "",
                billToZip: s.zip ?? "",
                billToPhone: s.phone ?? "",
                billToFax: s.fax ?? "",
                billToEmail: s.email ?? "",
            });
        } else if (checked) {
            update({ sameAsSendTo: true });
        } else {
            update({ sameAsSendTo: false });
        }
    };

    // AC9: "Transaction created" success card - replaces the form/footer entirely (master-demo-v6.html's `.txn-created` state), not an overlay on top of it.
    // Tidy-first (Spec #35135): the card itself moved to QuickEntryTransactionCreatedCard
    // so the Disclosure Accounting completion path (which skips this pane entirely)
    // can render the identical card instead of duplicating it. No markup change.
    if (props.transactionCreated) {
        return (
            <div className="qe-billing-pane qe-billing-pane--txn-created">
                <QuickEntryTransactionCreatedCard
                    requestId={props.transactionCreated.requestId}
                    onNextQueueItem={props.onNextQueueItem}
                />
            </div>
        );
    }

    return (
        <div className="qe-billing-pane">
            <div className="qe-billing-pane__header">
                <h2 className="qe-billing-pane__title">Billing Details</h2>
                <p className="qe-billing-pane__summary">
                    Complete billing to create the transaction. These fields are entered manually &mdash; the AI does not pre-fill billing.
                </p>
            </div>
            <div className="qe-billing-pane__body">
                {/*
                  Bug fix (Spec #34102 / F1d, direct user report - fresh
                  screenshot comparison found this missing from the initial
                  styling pass): master-demo-v6-standalone-src.html's own
                  .qe-pane__note--empty (line ~3367) - only shown before a
                  transaction exists yet, since Confirm-All routes here
                  without creating one (Request ID only generates on Create
                  Transaction below).
                */}
                <div className="qe-billing-pane__note qe-billing-pane__note--empty">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
                        <circle cx={12} cy={12} r={9} />
                        <path d="M12 8h.01M11 12h1v4h1" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    No transaction exists yet. Confirm All routed you here from Quick Entry &mdash; a Request ID is generated only when you create the transaction below.
                </div>
                <div className="qe-billing-pane__group">
                    <h3 className="qe-billing-pane__group-title">Fee Schedule &amp; Method</h3>
                    <div className="qe-billing-pane__field-2col">
                        <div className="qe-billing-pane__field">
                            <label htmlFor="qe-billing-fee-schedule">
                                Fee Schedule <span className="qe-billing-pane__req" aria-hidden="true">*</span>
                            </label>
                            <select
                                id="qe-billing-fee-schedule"
                                name="feeSchedule"
                                required
                                disabled={disableFeeSchedule}
                                value={feeScheduleIdOnOffer}
                                onChange={(e) => update({ feeScheduleId: e.target.value ? Number(e.target.value) : undefined })}
                            >
                                <option value="" disabled hidden>{defaultedControlPlaceholder}</option>
                                {props.feeSchedules.map(fs => <option key={fs.id} value={fs.id}>{fs.name}</option>)}
                            </select>
                        </div>
                        <div className="qe-billing-pane__field">
                            <label htmlFor="qe-billing-release-method">
                                Release Method <span className="qe-billing-pane__req" aria-hidden="true">*</span>
                            </label>
                            <select
                                id="qe-billing-release-method"
                                name="releaseMethod"
                                required
                                disabled={disableReleaseMethod}
                                value={fields.releaseMethodId ?? ""}
                                onChange={(e) => {
                                    // AB#38349: from here on the displayed value is the operator's, not
                                    // the server's, so the staleness suppression stops applying to it.
                                    setReleaseMethodPickedByOperator(true);
                                    update({ releaseMethodId: e.target.value ? Number(e.target.value) : undefined });
                                }}
                            >
                                <option value="" disabled hidden>{defaultedControlPlaceholder}</option>
                                {props.releaseMethods.map(rm => <option key={rm.id} value={rm.id}>{rm.name}</option>)}
                            </select>
                            {/* AB#35088: says WHY the two controls are inert, and keeps saying it for
                                the whole 8-18s the persist gate takes. role="status" so it is
                                announced when it appears. Rendered once, after the second of the two
                                controls it describes. */}
                            {defaultsPending ? (
                                <p className="qe-billing-pane__defaults-pending" role="status">
                                    <span className="qe-billing-pane__defaults-pending-spinner" aria-hidden="true" />
                                    {BILLING_DEFAULTS_PENDING_NOTE}
                                </p>
                            ) : null}
                            {/* AB#35095 (direct user feedback): Classic's own message
                                (localValidations.ts:370) - the gate was silent otherwise, leaving
                                the specialist looking at disabled buttons with no explanation.
                                Declarative (not blur-gated) since there is no email INPUT in this
                                pane to blur - the email comes from the requestor's contact. */}
                            {showMissingContactEmailWarning ? (
                                <span className="qe-billing-pane__field-error" role="alert">
                                    Requestor contact must have an email assigned for download delivery.
                                </span>
                            ) : null}
                            {/* AB#38349: the note that replaces THAT warning (the Download one) while
                                the release method is stale for the contact just selected. The Digital
                                Fax variant is rendered down in the fax block instead, beside the input
                                it is about (PR !10141 review finding 2). Deliberately NOT the AB#35088
                                defaults-pending presentation: that one carries a SPINNER, and there is
                                no round trip in flight here - the measurement is that the contact PATCH
                                does not re-point the transaction's link, so nothing settles until
                                Confirm All. A spinner would promise a process that is not running
                                (review round 4). role="status", not "alert": this is an explanation of
                                what is on screen, not the assertion of a validation failure. */}
                            {releaseMethodRecheckNote ? (
                                <p className="qe-billing-pane__release-method-recheck" role="status">
                                    {releaseMethodRecheckNote}
                                </p>
                            ) : null}
                        </div>
                    </div>
                    {/* Layout parity: Shipping Service | Charge for Postage, mirroring Classic's
                        second Fee-Schedule-tab row (DataEntryDetailsRightPane 2722-2751).
                        Rule #6/#8/#9: both are disabled for release method 2 (Fax) or an
                        internal request. */}
                    <div className="qe-billing-pane__field-2col">
                        <div className="qe-billing-pane__field">
                            <label htmlFor="qe-billing-shipping-service">Shipping Service</label>
                            <select
                                id="qe-billing-shipping-service"
                                name="shippingService"
                                disabled={disableShippingAndPostage}
                                value={fields.shippingService ?? ""}
                                onChange={(e) => update({ shippingService: e.target.value || undefined })}
                            >
                                {/* Bug 38059: props.shippingServices (state.dataEntryState.shippingService) is
                                    built by generatePickerOptions in the reducer, which already prepends its
                                    own "-Select-" default - a hardcoded placeholder here would duplicate it. */}
                                {(props.shippingServices ?? []).map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                            </select>
                        </div>
                        <div className="qe-billing-pane__field">
                            <label>Charge for Postage?</label>
                            {/* Classic RadioField options: Default | Yes | No (common/formFields/RadioField.tsx). */}
                            <div className="qe-billing-pane__radio-group" role="radiogroup" aria-label="Charge for Postage?">
                                {["Default", "Yes", "No"].map(opt => (
                                    <label key={opt} className="qe-billing-pane__radio">
                                        <input
                                            type="radio"
                                            name="chargeForPostage"
                                            value={opt}
                                            disabled={disableShippingAndPostage}
                                            checked={fields.chargeForPostage === opt}
                                            onChange={(e) => update({ chargeForPostage: e.target.value })}
                                        />
                                        {opt}
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                    {/* Rule #7: Digital Fax release method (id 3) reveals the Re-enter Fax Number
                        field (half width, left), mirroring Classic's showDigitalFax field. */}
                    {showDigitalFax ? (
                        <div className="qe-billing-pane__field-2col">
                            <div className="qe-billing-pane__field">
                                <label htmlFor="qe-billing-digital-fax">Re-enter Fax Number</label>
                                <input
                                    id="qe-billing-digital-fax"
                                    name="digitalFax"
                                    type="text"
                                    maxLength={12}
                                    placeholder="XXX-XXX-XXXX"
                                    value={fields.digitalFax ?? ""}
                                    // AB#37970 bonus scope (closes the orphaned AB#37975 duplicate): at the
                                    // time this looked like the only phone/fax field in Quick Entry with no
                                    // normalizePhone mask - it wasn't. billToPhone/billToFax below were
                                    // unmasked too and stayed that way until AB#38698 caught them.
                                    // Masking here is exact Classic parity, not a Quick-Entry-invented
                                    // restriction: Classic's own Re-enter Fax Number field is a
                                    // ReduxFormTextField whose name is in PHONE_FIELDS, so it runs
                                    // normalizePhone on every keystroke behind the same maxLength={12}.
                                    // This field is a CONFIRMATION input - it is never persisted as
                                    // contact data - so formatting it rewrites nothing a specialist owns.
                                    // The gate it feeds compares the two values after the SAME formatter
                                    // (digitalFaxMatchesOnFile above); the on-file fax is stored RAW, so an
                                    // exact string compare is what made the gate unsatisfiable.
                                    onChange={(e) => update({ digitalFax: sanitizeFieldInput("phone", e.target.value) })}
                                    onBlur={onDigitalFaxBlur}
                                />
                                {showDigitalFaxMismatchError ? (
                                    <span className="qe-billing-pane__field-error" role="alert">{digitalFaxError}</span>
                                ) : null}
                                {/* AB#38349, PR !10141 review finding 2: the stale-release-method note
                                    for THIS gate, in the block that owns the control it is about -
                                    it stands in for the red message that would otherwise sit on this
                                    very input, so rendering it one field up under Release Method left
                                    a screen-reader user hearing the remedy somewhere other than the
                                    field it names. role="alert", not "status", for the same reason:
                                    Create is disabled on this gate and the operator has to act, so
                                    replacing an assertive announcement with a polite one would have
                                    downgraded a blocker. (The Download variant keeps role="status" -
                                    it is not attached to any input in this pane, and it replaces a
                                    declarative warning in the slot it already occupied.) */}
                                {digitalFaxRecheckNote ? (
                                    <p className="qe-billing-pane__release-method-recheck" role="alert">
                                        {digitalFaxRecheckNote}
                                    </p>
                                ) : null}
                            </div>
                        </div>
                    ) : null}
                </div>

                {/* AC3: Certification - dropdown when AutoCertification is ON, checkbox when OFF (same split as Classic DataEntryDetailsRightPane 2771-2801). */}
                <div className="qe-billing-pane__group qe-billing-pane__group--certification">
                    <h3 className="qe-billing-pane__group-title">Certification</h3>
                    {/* Half width, left column only (2-col row with an empty right cell), matching Classic's Col xs={6}. */}
                    {autoCertification ? (
                        <div className="qe-billing-pane__field-2col">
                            <div className="qe-billing-pane__field">
                                <label htmlFor="qe-billing-certification-template">Certification Template</label>
                                <select
                                    id="qe-billing-certification-template"
                                    name="certificationTemplate"
                                    value={fields.certificationTemplateId ?? ""}
                                    onChange={(e) => update({ certificationTemplateId: e.target.value || undefined })}
                                >
                                    <option value="">- Select -</option>
                                    {(props.certificationTypes ?? []).map(ct => <option key={ct.id} value={ct.id}>{ct.name}</option>)}
                                </select>
                                <button
                                    type="button"
                                    className="qe-billing-pane__remove-certification-btn"
                                    disabled={removeCertificationDisabled}
                                    onClick={onRemoveCertification}
                                >
                                    Remove Certification
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="qe-billing-pane__field-2col">
                            <div className="qe-billing-pane__field qe-billing-pane__field--checkbox">
                                <input
                                    id="qe-billing-certification-requested"
                                    name="certificationRequested"
                                    type="checkbox"
                                    checked={Boolean(fields.certificationRequested)}
                                    onChange={(e) => update({ certificationRequested: e.target.checked })}
                                />
                                <label htmlFor="qe-billing-certification-requested">Certification Requested?</label>
                            </div>
                        </div>
                    )}
                </div>

                {/* Layout parity: Classic's Delivery Instructions multiline (DataEntryDetailsRightPane
                    2803-2819). Ordered here (after Certification, before Additional Charges + Bill To)
                    to match Classic's billing-tab order. Replaces the F1d Delivery Address/Notes
                    placeholders; persists via updateBillToRequestorData's deliveryInstructions key
                    (encodeURI) for ALL billing types - see DataEntryDetails.persistQuickEntryBillingFields. */}
                <div className="qe-billing-pane__group">
                    <h3 className="qe-billing-pane__group-title">Delivery Instructions</h3>
                    <div className="qe-billing-pane__field">
                        <label htmlFor="qe-billing-delivery-instructions">Delivery Instructions</label>
                        <textarea
                            id="qe-billing-delivery-instructions"
                            name="deliveryInstructions"
                            maxLength={2000}
                            value={fields.deliveryInstructions ?? ""}
                            onChange={(e) => update({ deliveryInstructions: e.target.value })}
                        />
                    </div>
                </div>

                {/* AC2: Additional charges - only when the selected fee schedule's billingTypeId === 1.
                    Layout parity: rendered as a collapsible accordion (collapsed by default), mirroring
                    Classic's Accordion.Toggle / Accordion.Collapse (Accordion.tsx 681-722). */}
                {additionalChargesEnabled ? (
                    <div className="qe-billing-pane__group qe-billing-pane__additional-charges">
                        <button
                            type="button"
                            className="qe-billing-pane__accordion-toggle"
                            aria-expanded={additionalChargesOpen}
                            aria-controls="qe-billing-additional-charges-body"
                            onClick={() => setAdditionalChargesOpen(open => !open)}
                        >
                            <span className="qe-billing-pane__group-title qe-billing-pane__accordion-title">Additional Charges</span>
                            <svg
                                className={`qe-billing-pane__accordion-chevron${additionalChargesOpen ? " qe-billing-pane__accordion-chevron--open" : ""}`}
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth={2}
                                aria-hidden="true"
                            >
                                <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                        <div
                            id="qe-billing-additional-charges-body"
                            className={`qe-billing-pane__accordion-body${additionalChargesOpen ? " qe-billing-pane__accordion-body--open" : ""}`}
                            hidden={!additionalChargesOpen}
                        >
                        {additionalCharges.map((charge, index) => (
                            <div className="qe-billing-pane__charge-row" key={`charge-${index}`}>
                                <div className="qe-billing-pane__field">
                                    <label htmlFor={`qe-billing-charge-type-${index}`}>
                                        Charge Type <span className="qe-billing-pane__req" aria-hidden="true">*</span>
                                    </label>
                                    <select
                                        id={`qe-billing-charge-type-${index}`}
                                        name={`chargeType-${index}`}
                                        value={charge.glCodeID ?? ""}
                                        onChange={(e) => updateCharge(index, { glCodeID: e.target.value || undefined })}
                                    >
                                        <option value="" disabled hidden>- Select -</option>
                                        {(props.additionalChargeTypeOptions ?? []).map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                                    </select>
                                </div>
                                <div className="qe-billing-pane__field">
                                    <label htmlFor={`qe-billing-charge-description-${index}`}>
                                        Description <span className="qe-billing-pane__req" aria-hidden="true">*</span>
                                    </label>
                                    <input
                                        id={`qe-billing-charge-description-${index}`}
                                        name={`chargeDescription-${index}`}
                                        type="text"
                                        maxLength={200}
                                        value={charge.description ?? ""}
                                        onChange={(e) => updateCharge(index, { description: e.target.value })}
                                    />
                                </div>
                                <div className="qe-billing-pane__field">
                                    <label htmlFor={`qe-billing-charge-amount-${index}`}>
                                        Amount <span className="qe-billing-pane__req" aria-hidden="true">*</span>
                                    </label>
                                    <input
                                        id={`qe-billing-charge-amount-${index}`}
                                        name={`chargeAmount-${index}`}
                                        type="text"
                                        value={charge.chargeAmount ?? ""}
                                        onChange={(e) => updateCharge(index, { chargeAmount: e.target.value })}
                                        onBlur={(e) => updateCharge(index, { chargeAmount: formatChargeAmount(e.target.value) })}
                                    />
                                </div>
                                <button
                                    type="button"
                                    className="qe-billing-pane__remove-charge-btn"
                                    onClick={() => removeCharge(index)}
                                >
                                    Remove
                                </button>
                            </div>
                        ))}
                        <button
                            type="button"
                            className="qe-billing-pane__add-charge-btn"
                            disabled={addChargeDisabled}
                            onClick={addCharge}
                        >
                            + Add Charge
                        </button>
                        </div>
                    </div>
                ) : null}

                {/* AC1/AC4: Bill To - prepayment, "Same as send to", and the full bill-to address set (replaces the old flat blank stub). PII/PHI.
                    Rule #10: shown ONLY when the selected fee schedule's billingTypeId === 1
                    (Classic gates the whole bill-to + charges accordion on getBillingTypeId()==1). */}
                {billingTypeOne ? (
                <div className="qe-billing-pane__group qe-billing-pane__bill-to">
                    <h3 className="qe-billing-pane__group-title">Bill To</h3>
                    {/* Left-aligned 2-col rows matching Classic's Bill To block (Accordion.tsx). */}
                    <div className="qe-billing-pane__field-2col">
                        <div className="qe-billing-pane__field qe-billing-pane__field--checkbox">
                            <input
                                id="qe-billing-same-as-send-to"
                                name="sameAsSendTo"
                                type="checkbox"
                                checked={Boolean(fields.sameAsSendTo)}
                                onChange={(e) => onToggleSameAsSendTo(e.target.checked)}
                            />
                            <label htmlFor="qe-billing-same-as-send-to">Same as send to</label>
                        </div>
                        <div className="qe-billing-pane__field qe-billing-pane__field--checkbox">
                            <input
                                id="qe-billing-requires-prepayment"
                                name="requiresPrepayment"
                                type="checkbox"
                                checked={Boolean(fields.requiresPrepayment)}
                                onChange={(e) => update({ requiresPrepayment: e.target.checked })}
                            />
                            <label htmlFor="qe-billing-requires-prepayment">Requires Prepayment?</label>
                        </div>
                    </div>
                    <div className="qe-billing-pane__field-2col">
                        <div className="qe-billing-pane__field">
                            <label htmlFor="qe-billing-bill-to-requestor-name">
                                Requestor name <span className="qe-billing-pane__req" aria-hidden="true">*</span>
                            </label>
                            <input
                                id="qe-billing-bill-to-requestor-name"
                                name="billToRequestorName"
                                type="text"
                                maxLength={100}
                                value={fields.billToRequestorName ?? ""}
                                onChange={(e) => update({ billToRequestorName: e.target.value })}
                            />
                        </div>
                        <div className="qe-billing-pane__field">
                            <label htmlFor="qe-billing-bill-to-first-name">First name</label>
                            <input
                                id="qe-billing-bill-to-first-name"
                                name="billToFirstName"
                                type="text"
                                maxLength={30}
                                value={fields.billToFirstName ?? ""}
                                onChange={(e) => update({ billToFirstName: e.target.value })}
                            />
                        </div>
                    </div>
                    <div className="qe-billing-pane__field-2col">
                        <div className="qe-billing-pane__field">
                            <label htmlFor="qe-billing-bill-to-last-name">Last name</label>
                            <input
                                id="qe-billing-bill-to-last-name"
                                name="billToLastName"
                                type="text"
                                maxLength={30}
                                value={fields.billToLastName ?? ""}
                                onChange={(e) => update({ billToLastName: e.target.value })}
                            />
                        </div>
                        <div className="qe-billing-pane__field">
                            <label htmlFor="qe-billing-bill-to-address-line1">
                                Address <span className="qe-billing-pane__req" aria-hidden="true">*</span>
                            </label>
                            <input
                                id="qe-billing-bill-to-address-line1"
                                name="billToAddressLine1"
                                type="text"
                                maxLength={70}
                                placeholder="Address line 1"
                                value={fields.billToAddressLine1 ?? ""}
                                onChange={(e) => update({ billToAddressLine1: e.target.value })}
                            />
                        </div>
                    </div>
                    <div className="qe-billing-pane__field-2col">
                        <div className="qe-billing-pane__field">
                            <label htmlFor="qe-billing-bill-to-address-line2">Address line 2</label>
                            <input
                                id="qe-billing-bill-to-address-line2"
                                name="billToAddressLine2"
                                type="text"
                                maxLength={70}
                                placeholder="P.O. Box, Suite, Apt #"
                                value={fields.billToAddressLine2 ?? ""}
                                onChange={(e) => update({ billToAddressLine2: e.target.value })}
                            />
                        </div>
                        <div className="qe-billing-pane__field">
                            <label htmlFor="qe-billing-bill-to-city">
                                City <span className="qe-billing-pane__req" aria-hidden="true">*</span>
                            </label>
                            <input
                                id="qe-billing-bill-to-city"
                                name="billToCity"
                                type="text"
                                maxLength={30}
                                value={fields.billToCity ?? ""}
                                onChange={(e) => update({ billToCity: e.target.value })}
                            />
                        </div>
                    </div>
                    <div className="qe-billing-pane__field-2col">
                        <div className="qe-billing-pane__field">
                            <label htmlFor="qe-billing-bill-to-state">
                                State <span className="qe-billing-pane__req" aria-hidden="true">*</span>
                            </label>
                            <select
                                id="qe-billing-bill-to-state"
                                name="billToState"
                                value={fields.billToState ?? ""}
                                onChange={(e) => update({ billToState: e.target.value || undefined })}
                            >
                                <option value="" disabled hidden>- Select -</option>
                                {UNITED_STATES.map(st => <option key={st.abbreviation} value={st.abbreviation}>{st.abbreviation}</option>)}
                            </select>
                        </div>
                        <div className="qe-billing-pane__field">
                            <label htmlFor="qe-billing-bill-to-zip">
                                Zip code <span className="qe-billing-pane__req" aria-hidden="true">*</span>
                            </label>
                            <input
                                id="qe-billing-bill-to-zip"
                                name="billToZip"
                                type="text"
                                maxLength={10}
                                value={fields.billToZip ?? ""}
                                // AB#37973 - deliberately NOT masked, after adversarial review.
                                // Wiring this to the postalCode kind (digits + one hyphen) silently
                                // destroyed every non-US postal code: a Canadian "K1A 0B1" became "101"
                                // and saved with no error, and aspTest alone holds 15,212 billing rows
                                // whose zip is not 5-or-9 digits. Classic does not truncate either - it
                                // validates billZip post-hoc (localValidations.ts's zipRegex) and shows
                                // the specialist an error, which is what billToZipFormatInvalid above
                                // now does. Same conclusion as AB#37970's phone extension and
                                // AB#37972's Request ID on this branch: telling someone their input is
                                // wrong is fine, silently rewriting it is not.
                                onChange={(e) => update({ billToZip: e.target.value })}
                            />
                            {billToZipFormatInvalid ? (
                                <span className="qe-billing-pane__field-error" role="alert">Enter valid zip code.</span>
                            ) : null}
                        </div>
                    </div>
                    <div className="qe-billing-pane__field-2col">
                        <div className="qe-billing-pane__field">
                            <label htmlFor="qe-billing-bill-to-phone">Phone</label>
                            <input
                                id="qe-billing-bill-to-phone"
                                name="billToPhone"
                                type="text"
                                placeholder="XXX-XXX-XXXX"
                                maxLength={12}
                                value={fields.billToPhone ?? ""}
                                // AB#38698: unlike Bill To Zip above (deliberately unmasked - see that
                                // field's comment), Classic already masks this field - BILL_TO_PHONE is a
                                // member of ReduxFormTextField's PHONE_FIELDS - so silently rewriting it is
                                // parity, not a Quick-Entry-invented restriction.
                                onChange={(e) => update({ billToPhone: sanitizeFieldInput("phone", e.target.value) })}
                            />
                        </div>
                        <div className="qe-billing-pane__field">
                            <label htmlFor="qe-billing-bill-to-fax">Fax</label>
                            <input
                                id="qe-billing-bill-to-fax"
                                name="billToFax"
                                type="text"
                                placeholder="XXX-XXX-XXXX"
                                maxLength={12}
                                value={fields.billToFax ?? ""}
                                // AB#38698: same rationale as billToPhone above - BILL_TO_FAX is also a
                                // PHONE_FIELDS member in Classic.
                                onChange={(e) => update({ billToFax: sanitizeFieldInput("phone", e.target.value) })}
                            />
                        </div>
                    </div>
                    <div className="qe-billing-pane__field-2col">
                        <div className="qe-billing-pane__field">
                            <label htmlFor="qe-billing-bill-to-email">Email</label>
                            <input
                                id="qe-billing-bill-to-email"
                                name="billToEmail"
                                type="text"
                                maxLength={100}
                                value={fields.billToEmail ?? ""}
                                onChange={(e) => update({ billToEmail: e.target.value })}
                            />
                        </div>
                    </div>
                </div>
                ) : null}
            </div>
            <div className="qe-billing-pane__footer">
                <button type="button" className="qe-billing-pane__back-btn" onClick={props.onBack}>
                    &larr; Back to Quick Entry
                </button>
                <div className="qe-billing-pane__footer-actions">
                    <button
                        type="button"
                        className="qe-billing-pane__create-and-continue-btn"
                        disabled={creationDisabled}
                        onClick={() => props.onCreateAndContinue(fields)}
                    >
                        Create &amp; Continue to Record Attachment
                    </button>
                    <button
                        type="button"
                        className="qe-billing-pane__create-transaction-btn"
                        disabled={creationDisabled}
                        onClick={() => props.onCreateTransaction(fields)}
                    >
                        <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.7} aria-hidden="true">
                            <circle cx={10} cy={10} r={8.5} />
                            <path d="M6 10.2l2.6 2.6L14 7" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        Create Transaction
                    </button>
                </div>
            </div>
        </div>
    );
};

export default QuickEntryBillingPane;
